/**
 * Video autoplay helper for the Video section.
 *
 * Goal:
 * - When `data-autoplay="true"` is present on `[data-video-external-target]`, attempt to autoplay the video silently.
 * - For Shopify-hosted videos (`.video__video`), ensure `muted` and call `play()`.
 * - For external videos, best-effort: patch iframe src with autoplay/mute params when the iframe is injected.
 *
 * This file is meant to be loaded AFTER `theme.min.js` (or `theme.js` in dev).
 */

function hideVideoCover(container) {
  if (!container) return;
  const overlay = container.querySelector(".video__overlay");
  const text = container.querySelector(".video__text-container-wrapper");
  const image = container.querySelector(".video__image");

  overlay && overlay.classList.remove("visible");
  text && text.classList.remove("visible");
  image && image.classList.remove("visible");
}

function tryPlayShopifyVideo(container) {
  const video = container.querySelector(".video__video");
  if (!video) return;

  // Ensure muted autoplay is allowed
  video.muted = true;
  video.setAttribute("muted", "");
  video.setAttribute("playsinline", "");

  // Hide cover once we actually start playing
  video.addEventListener(
    "playing",
    () => {
      hideVideoCover(container);
    },
    { once: true }
  );

  // Best-effort play (can still be blocked by browser policies)
  const playPromise = video.play();
  if (playPromise && typeof playPromise.catch === "function") {
    playPromise.catch(() => {});
  }
}

function patchExternalIframeSrc(videoExternalEl) {
  const iframe = videoExternalEl.querySelector("iframe");
  if (!iframe || !iframe.src) return false;

  const provider = (videoExternalEl.dataset.videoProvider || "").toLowerCase();

  try {
    const url = new URL(iframe.src, window.location.href);

    // Autoplay ON
    url.searchParams.set("autoplay", "1");

    // Mute ON (provider-specific)
    if (provider === "youtube") {
      url.searchParams.set("mute", "1");
      url.searchParams.set("playsinline", "1");
    } else if (provider === "vimeo") {
      url.searchParams.set("muted", "1");
      url.searchParams.set("playsinline", "1");
    }

    const nextSrc = url.toString();
    if (iframe.src !== nextSrc) {
      iframe.src = nextSrc;
    }
  } catch (e) {
    // If URL parsing fails, do nothing (best-effort).
  }

  return true;
}

function initVideoSectionAutoplay(container) {
  if (!container) return;

  const containerAutoplay = container.dataset.autoplay === "true";

  // External video: only if flagged by Liquid.
  const videoExternal = container.querySelector("[data-video-external-target]");
  const externalAutoplay = videoExternal ? videoExternal.dataset.autoplay === "true" : false;

  const shouldAutoplay = containerAutoplay || externalAutoplay;
  if (!shouldAutoplay) return;

  // Shopify-hosted video: autoplay is rendered via Liquid, we just help browsers by forcing mute + play().
  tryPlayShopifyVideo(container);

  if (!videoExternal) return;

  // When the iframe is injected by the theme code, patch the URL to enforce autoplay + mute.
  const tryPatch = () => {
    const didPatch = patchExternalIframeSrc(videoExternal);
    if (didPatch) {
      // Hide cover once iframe loads (best-effort signal that playback may start)
      const iframe = videoExternal.querySelector("iframe");
      if (iframe) {
        iframe.addEventListener(
          "load",
          () => {
            hideVideoCover(container);
          },
          { once: true }
        );
      }
    }
    return didPatch;
  };

  if (tryPatch()) return;

  const observer = new MutationObserver(() => {
    if (tryPatch()) {
      observer.disconnect();
    }
  });

  observer.observe(videoExternal, { childList: true, subtree: true });

  // Fallback: stop observing after a few seconds
  window.setTimeout(() => observer.disconnect(), 5000);
}

function initVideoAutoplay(root) {
  const scope = root && root.querySelectorAll ? root : document;
  scope.querySelectorAll('[data-section-type="video"]').forEach((container) => {
    initVideoSectionAutoplay(container);
  });
}

// Initial load
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => initVideoAutoplay(document));
} else {
  initVideoAutoplay(document);
}

// Theme editor / dynamic section reloads
document.addEventListener("shopify:section:load", (event) => {
  initVideoAutoplay(event.target);
});

